import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Sparkles, MessageSquare, ChevronRight, Lightbulb } from 'lucide-react';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';

interface AIAssistantWidgetProps {
  isLoaded?: boolean;
}

const quickSuggestions = [
  'Quais projetos estão atrasados?',
  'Resumo financeiro deste mês',
  'Tarefas para hoje',
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.5
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, x: -15 },
  visible: { 
    opacity: 1, 
    x: 0,
    transition: {
      type: "spring" as const,
      stiffness: 100,
      damping: 12
    }
  }
};

export function AIAssistantWidget({ isLoaded = true }: AIAssistantWidgetProps) {
  const navigate = useNavigate();

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
    >
      <Card className="bento-card h-full bg-gradient-to-br from-primary/5 via-background to-accent/5 overflow-hidden relative">
        {/* Animated background glow */}
        <motion.div
          className="absolute inset-0 bg-gradient-to-r from-primary/10 via-transparent to-accent/10 opacity-0"
          animate={{ opacity: [0, 0.5, 0] }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
        />
        
        <CardHeader className="pb-3 relative">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.7, type: "spring", stiffness: 200 }}
              >
                <motion.div
                  animate={{ 
                    rotate: [0, 5, -5, 0],
                    scale: [1, 1.1, 1]
                  }}
                  transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
                >
                  <Sparkles className="h-5 w-5 text-primary" />
                </motion.div>
              </motion.div>
              <motion.span
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.8 }}
              >
                Assistente IA
              </motion.span>
            </CardTitle>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9 }}
            >
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-xs text-muted-foreground hover:text-foreground"
                onClick={() => navigate('/agente-ia')}
              >
                Abrir chat
                <ChevronRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            </motion.div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4 relative">
          <motion.div 
            className="p-3 rounded-xl bg-primary/5 border border-primary/10"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1, type: "spring" }}
          >
            <div className="flex items-start gap-2">
              <motion.div
                animate={{ 
                  rotate: [0, 10, -10, 0],
                  y: [0, -2, 0]
                }}
                transition={{ duration: 2, repeat: Infinity, repeatDelay: 2 }}
              >
                <Lightbulb className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              </motion.div>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Use o assistente IA para obter insights sobre seus projetos, 
                gerar relatórios e responder dúvidas sobre seus dados.
              </p>
            </div>
          </motion.div>
          
          <div>
            <motion.p 
              className="text-xs font-medium text-muted-foreground mb-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.1 }}
            >
              Perguntas rápidas:
            </motion.p>
            <motion.div 
              className="space-y-1.5"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {quickSuggestions.map((question, index) => (
                <motion.button
                  key={index}
                  variants={itemVariants}
                  whileHover={{ 
                    scale: 1.02, 
                    x: 5,
                    transition: { duration: 0.2 }
                  }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full text-left text-xs p-2.5 rounded-lg bg-muted/50 hover:bg-muted transition-colors text-foreground/80 hover:text-foreground flex items-center gap-2"
                  onClick={() => navigate('/agente-ia', { state: { initialQuestion: question } })}
                >
                  <MessageSquare className="h-3 w-3 text-primary shrink-0" />
                  {question}
                </motion.button>
              ))}
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.4 }}
          >
            <Button 
              className="w-full relative overflow-hidden group"
              onClick={() => navigate('/agente-ia')}
            >
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-primary/0 via-primary-foreground/10 to-primary/0"
                animate={{ x: ['-100%', '100%'] }}
                transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
              />
              <Sparkles className="h-4 w-4 mr-2 relative" />
              <span className="relative">Conversar com IA</span>
            </Button>
          </motion.div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
